import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { getImageUrlPlant } from '@/features/plants/hooks/queries/[plantId]/get-image-url.ts';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';

export type PostPlantResponse = {
  body: {
    plantId: string;
    message: string;
  };
};

export const usePostPlantQuery = ({
  onSuccess,
}: {
  onSuccess: (data: PostPlantResponse) => void;
}) => {
  const createPlantWithImages = async (plant: TPlant): Promise<PostPlantResponse> => {
    const { data } = await client.post<PostPlantResponse>('/plants', {
      basicDetails: plant.basicDetails,
      siteInformation: {
        ...plant.siteInformation,
        image: '',
      },
      technicalSpecifications: plant.technicalSpecifications,
      equipmentAndConnectionDetails: plant.equipmentAndConnectionDetails,
      financialInformation: plant.financialInformation,
      contactDetails: plant.contactDetails,
    });

    if (!data?.body?.plantId) {
      throw new Error('No plant ID returned from server.');
    }

    await uploadImages(plant.siteInformation.image, data.body.plantId);

    return data;
  };

  return useMutation({
    mutationFn: (plant: TPlant) => {
      return toast.promise(createPlantWithImages(plant), {
        loading: 'Creating plant...',
        success: 'Successfully created the plant.',
        error: (_: unknown) => `Uh oh, there was an error with creating the plant.`,
      });
    },
    onSuccess: (data) => {
      if (data) {
        onSuccess(data);
      }
    },
  });
};

const uploadImages = async (images: unknown, plantId: string): Promise<void> => {
  if (!Array.isArray(images) || images.length === 0) return;

  const uploadPromises = images
    .filter((image): image is File => image instanceof File)
    .map(async (image) => {
      try {
        const response = await getImageUrlPlant({
          id: plantId,
          file: image,
        });

        if (response?.uploadUrl) {
          await axios.put(response.uploadUrl, image);
        }
      } catch (_: unknown) {
        toast.error(`Failed to upload plant image.`);
      }
    });

  await Promise.allSettled(uploadPromises);
};
